/**
 * RemindersWidget — surfaces dated editorial reminders on the admin
 * dashboard. Reads `GET /api/admin/reminders`, which parses
 * `/app/memory/REMINDERS.md` and returns entries sorted so due /
 * overdue items appear first.
 *
 * The widget is intentionally quiet when nothing is due: a small
 * neutral card so operators don't get "empty state fatigue". When
 * items are due, it flips to an amber accent and shows up to three
 * top entries with a "days overdue / until" chip.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BellRing, CalendarClock, Loader2 } from "lucide-react";

function DueChip({ days }) {
  const overdue = days <= 0;
  const label =
    days === 0
      ? "Due today"
      : overdue
      ? `${Math.abs(days)}d overdue`
      : `in ${days}d`;
  return (
    <span
      data-testid="reminder-due-chip"
      className={
        "inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border px-1.5 py-0.5 " +
        (overdue
          ? "border-amber-400 bg-amber-50 text-amber-900"
          : "border-stone-300 bg-white text-stone-600")
      }
    >
      <CalendarClock className="h-2.5 w-2.5" aria-hidden />
      {label}
    </span>
  );
}

export default function RemindersWidget() {
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/reminders")
      .then((r) => alive && setState({ loading: false, data: r.data, err: null }))
      .catch((e) => alive && setState({ loading: false, data: null, err: e }));
    return () => {
      alive = false;
    };
  }, []);

  if (state.loading) {
    return (
      <div
        data-testid="reminders-widget-loading"
        className="rounded-md border border-stone-200 bg-white p-4 text-[12px] text-stone-500 flex items-center gap-2"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading editorial reminders…
      </div>
    );
  }

  if (state.err) {
    return (
      <div
        data-testid="reminders-widget-error"
        className="rounded-md border border-rose-200 bg-rose-50/60 p-4 text-[12px] text-rose-800"
      >
        Couldn't load reminders — check the /admin/reminders endpoint.
      </div>
    );
  }

  const entries = state.data?.entries || [];
  const dueCount = state.data?.due_count || 0;

  if (!entries.length) {
    return (
      <div
        data-testid="reminders-widget-empty"
        className="rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-4 text-[12px] text-stone-500"
      >
        <div className="font-underground-sc tracking-wider text-[10px] text-stone-400">
          Editorial reminders
        </div>
        <div className="mt-1">
          Nothing logged yet. Add entries in <code>/app/memory/REMINDERS.md</code>.
        </div>
      </div>
    );
  }

  // Show up to 3 items — enough to be useful, few enough not to hog
  // dashboard real estate. Due items always float to the top.
  const top = entries.slice(0, 3);
  const accent = dueCount > 0;

  return (
    <div
      data-testid="reminders-widget"
      className={
        "rounded-md border p-4 " +
        (accent
          ? "border-amber-300 bg-amber-50/60"
          : "border-stone-200 bg-white")
      }
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BellRing
            className={"h-3.5 w-3.5 " + (accent ? "text-amber-700" : "text-stone-500")}
          />
          <div
            data-testid="reminders-widget-heading"
            className={
              "label-uppercase font-semibold " +
              (accent ? "text-amber-900" : "text-stone-700")
            }
          >
            Editorial reminders
            {dueCount > 0 && (
              <span
                data-testid="reminders-widget-due-count"
                className="ml-2 text-[10px] font-mono normal-case rounded-full bg-amber-200 text-amber-900 px-1.5 py-0.5"
              >
                {dueCount} due
              </span>
            )}
          </div>
        </div>
        {state.data?.next_due && dueCount === 0 && (
          <div className="text-[10px] font-mono text-stone-400">
            next: {state.data.next_due}
          </div>
        )}
      </div>

      <ul className="mt-3 space-y-2">
        {top.map((e) => (
          <li
            key={e.date + e.title}
            data-testid={`reminder-${e.date}`}
            className="flex items-start justify-between gap-3 text-[12.5px]"
          >
            <div className="min-w-0">
              <div className="text-stone-800 truncate font-medium">{e.title}</div>
              <div className="text-[11px] font-mono text-stone-500">{e.date}</div>
            </div>
            <DueChip days={e.days_until_due} />
          </li>
        ))}
      </ul>

      {entries.length > top.length && (
        <div className="mt-3 pt-2 border-t border-stone-100 text-[10px] font-mono text-stone-400">
          +{entries.length - top.length} more in /app/memory/REMINDERS.md
        </div>
      )}
    </div>
  );
}
